import { primeMessageBodyCacheFromIndexedDB } from "@/lib/cache/messageBodyCache";
import {
  INBOX_CLIENT_HYDRATE_MAX,
  INBOX_CLIENT_HYDRATE_WAVE,
  INBOX_CLIENT_HYDRATE_WAVE_GAP_MS,
} from "@/lib/constants/inbox";
import { hydrateBodiesFromServerBootstrap } from "@/lib/mail/bodiesBootstrap";
import { primeMessageBodyFromServer } from "@/lib/mail/fetchMessageBodyWithCache";
import {
  completeMailActivity,
  discardMailActivity,
  startMailActivity,
} from "@/lib/mail/mailActivityJournalNote";
import {
  setMailMutationLabel,
  setMailMutationProgress,
} from "@/lib/mail/mailMutationProgress";
import { isMessageBodyCached } from "@/lib/mail/messageBodyCacheStatus";

/** Alias — plafond hydratation client par palier (100). */
export const INBOX_VISIBLE_READY_PRIME_MAX = INBOX_CLIENT_HYDRATE_MAX;

/** Fusion des appels liste (scroll / fingerprint) avant de lancer un job. */
const HYDRATE_SCHEDULE_DEBOUNCE_MS = 400;

export type VisibleReadyPrimeEntry = {
  accountId: number;
  folder: string;
  uid: number;
  /** Non-lus en premier au sein d’une vague. */
  priority?: boolean;
};

type HydrateGroup = {
  accountId: number;
  folder: string;
  uids: number[];
  priority: Set<number>;
};

/** No-op — conservé pour useMailMessage. */
export function pauseClientHydration(): void {}
export function resumeClientHydration(): void {}

/**
 * Miss DD02 session : bootstrap + check=1 sans corps lisible.
 * Évite de rejouer / journaliser les mêmes UID (ex. 0/6 en boucle).
 */
const dd02HydrateMissKeys = new Set<string>();

function entryKey(accountId: number, folder: string, uid: number): string {
  return `${accountId}\0${folder}\0${uid}`;
}

function markDd02HydrateMiss(accountId: number, folder: string, uid: number): void {
  if (uid < 1) return;
  const key = entryKey(accountId, folder, uid);
  dd02HydrateMissKeys.add(key);
  pendingByKey.delete(key);
}

/** True si déjà en cache client **ou** tenté DD02 sans succès (session). */
export function isClientHydrateSettled(
  accountId: number,
  folder: string,
  uid: number
): boolean {
  if (uid < 1) return true;
  if (isMessageBodyCached(accountId, folder, uid)) {
    dd02HydrateMissKeys.delete(entryKey(accountId, folder, uid));
    return true;
  }
  return dd02HydrateMissKeys.has(entryKey(accountId, folder, uid));
}

function groupKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function yieldToUi(): Promise<void> {
  return new Promise((resolve) => {
    const ric = (
      window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    if (typeof ric === "function") {
      ric(() => resolve(), { timeout: INBOX_CLIENT_HYDRATE_WAVE_GAP_MS });
      return;
    }
    window.setTimeout(resolve, 0);
  });
}

/** File d’attente fusionnée — un seul runner, pas de kill/restart. */
const pendingByKey = new Map<string, VisibleReadyPrimeEntry>();
let debounceTimer: number | null = null;
/** Chaîne séquentielle : jamais deux batches en parallèle. */
let hydrateChain: Promise<void> = Promise.resolve();
let activeScope: string | null = null;

function mergePending(entries: VisibleReadyPrimeEntry[]): void {
  for (const entry of entries) {
    if (entry.uid < 1 || entry.accountId < 1) continue;
    if (isClientHydrateSettled(entry.accountId, entry.folder, entry.uid)) continue;
    const key = entryKey(entry.accountId, entry.folder, entry.uid);
    const prev = pendingByKey.get(key);
    pendingByKey.set(key, {
      ...entry,
      priority: Boolean(entry.priority) || Boolean(prev?.priority),
    });
  }
}

/** Au changement de dossier/compte : ne garder que le scope demandé. */
function trimPendingToScope(scope: string): void {
  if (activeScope === scope) return;
  activeScope = scope;
  if (pendingByKey.size === 0) return;
  // Scope = `accountId:folder` ou `unified:folder` — on ne purge que si dossier change.
  const scopeFolder = scope.includes(":") ? scope.slice(scope.indexOf(":") + 1) : scope;
  for (const [key, entry] of [...pendingByKey.entries()]) {
    if (entry.folder !== scopeFolder) {
      pendingByKey.delete(key);
    }
  }
}

function takeBatch(max: number): HydrateGroup[] {
  const sorted = [...pendingByKey.values()].sort((a, b) => {
    if (b.uid !== a.uid) return b.uid - a.uid;
    const pa = a.priority ? 0 : 1;
    const pb = b.priority ? 0 : 1;
    return pa - pb;
  });

  const byFolder = new Map<string, HydrateGroup>();
  let accepted = 0;

  for (const entry of sorted) {
    if (accepted >= max) break;
    const key = entryKey(entry.accountId, entry.folder, entry.uid);
    if (!pendingByKey.has(key)) continue;
    if (isClientHydrateSettled(entry.accountId, entry.folder, entry.uid)) {
      pendingByKey.delete(key);
      continue;
    }

    const gk = groupKey(entry.accountId, entry.folder);
    let group = byFolder.get(gk);
    if (!group) {
      group = {
        accountId: entry.accountId,
        folder: entry.folder,
        uids: [],
        priority: new Set(),
      };
      byFolder.set(gk, group);
    }
    if (group.uids.includes(entry.uid)) {
      pendingByKey.delete(key);
      continue;
    }
    group.uids.push(entry.uid);
    if (entry.priority) group.priority.add(entry.uid);
    pendingByKey.delete(key);
    accepted += 1;
  }

  return [...byFolder.values()];
}

function countCached(groups: HydrateGroup[]): number {
  let n = 0;
  for (const group of groups) {
    for (const uid of group.uids) {
      if (isMessageBodyCached(group.accountId, group.folder, uid)) n += 1;
    }
  }
  return n;
}

function batchTotal(groups: HydrateGroup[]): number {
  return groups.reduce((sum, g) => sum + g.uids.length, 0);
}

async function runHydrateBatch(groups: HydrateGroup[]): Promise<void> {
  const total = batchTotal(groups);
  if (total < 1) return;

  const waveCount = Math.max(1, Math.ceil(total / INBOX_CLIENT_HYDRATE_WAVE));
  const journalId = startMailActivity(
    total === 1
      ? "Hydratation · 1 message"
      : `Hydratation · ${total} messages (${waveCount} lots)`,
    "sync",
    "local"
  );
  setMailMutationProgress(journalId, 0, total);

  let done = 0;
  let waveIndex = 0;

  const bumpFromCache = () => {
    done = countCached(groups);
    setMailMutationProgress(journalId, done, total);
    setMailMutationLabel(
      journalId,
      `Hydratation · lot ${Math.min(Math.max(waveIndex, 1), waveCount)}/${waveCount} · ${done}/${total} écrits`
    );
  };

  try {
    await yieldToUi();

    for (const group of groups) {
      await primeMessageBodyCacheFromIndexedDB(
        group.accountId,
        group.folder,
        group.uids,
        group.priority
      );
      bumpFromCache();

      const need = group.uids.filter(
        (uid) => !isMessageBodyCached(group.accountId, group.folder, uid)
      );
      if (need.length === 0) continue;

      for (let i = 0; i < need.length; i += INBOX_CLIENT_HYDRATE_WAVE) {
        waveIndex += 1;
        const wave = need.slice(i, i + INBOX_CLIENT_HYDRATE_WAVE);
        setMailMutationLabel(
          journalId,
          `Hydratation · lot ${waveIndex}/${waveCount} · ${done}/${total} écrits`
        );

        await hydrateBodiesFromServerBootstrap(
          group.accountId,
          group.folder,
          wave,
          {
            priorityUids: group.priority,
            maxUids: wave.length,
          }
        );
        bumpFromCache();

        // Secours DD02 only (check=1) — jamais d’IMAP.
        const still = wave.filter(
          (uid) => !isMessageBodyCached(group.accountId, group.folder, uid)
        );
        for (const uid of still) {
          await primeMessageBodyFromServer(group.accountId, group.folder, uid, {
            dd02Only: true,
            quick: true,
            timeoutMs: 8_000,
          });
          bumpFromCache();
          if (!isMessageBodyCached(group.accountId, group.folder, uid)) {
            markDd02HydrateMiss(group.accountId, group.folder, uid);
          }
          await delay(0);
        }

        if (i + INBOX_CLIENT_HYDRATE_WAVE < need.length) {
          await delay(INBOX_CLIENT_HYDRATE_WAVE_GAP_MS);
          await yieldToUi();
        }
      }
    }

    bumpFromCache();
    // DD02 pas encore prêt (warm / .eml en cours) : ne pas polluer le journal.
    if (done < 1) {
      discardMailActivity(journalId);
    } else if (done >= total) {
      completeMailActivity(journalId, `Hydratation terminée · ${done} messages`);
    } else {
      completeMailActivity(journalId, `Hydratation partielle · ${done}/${total} écrits`);
    }
  } catch {
    if (done < 1) {
      discardMailActivity(journalId);
    } else {
      completeMailActivity(journalId, `Hydratation partielle · ${done}/${total}`);
    }
  }
}

async function drainHydrateQueue(max: number): Promise<void> {
  while (pendingByKey.size > 0) {
    const groups = takeBatch(max);
    if (groups.length === 0) break;
    await runHydrateBatch(groups);
    await yieldToUi();
  }
}

function kickHydrate(max: number): void {
  if (pendingByKey.size === 0) return;
  hydrateChain = hydrateChain
    .then(() => drainHydrateQueue(max))
    .catch(() => {
      /* une vague KO ne casse pas la file */
    });
}

/**
 * Hydrate client en fond — **DD02 only** (jamais d’IMAP : freeze).
 * Debounce + file fusionnée : un job à la fois (plus de rafale « interrompue »).
 * IDB → bodies-bootstrap → check=1 unitaire si besoin.
 */
export function schedulePrimeVisibleReadyBodies(
  entries: VisibleReadyPrimeEntry[],
  max = INBOX_CLIENT_HYDRATE_MAX,
  scope = "default"
): void {
  if (entries.length === 0 || max < 1) return;

  trimPendingToScope(scope);
  mergePending(entries);
  if (pendingByKey.size === 0) return;

  if (debounceTimer != null) {
    window.clearTimeout(debounceTimer);
  }
  debounceTimer = window.setTimeout(() => {
    debounceTimer = null;
    kickHydrate(max);
  }, HYDRATE_SCHEDULE_DEBOUNCE_MS);
}
